<template>
  <div class="article-page">
    <!-- 面包屑导航 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <span class="current">2026年央企数字化转型趋势报告</span>
    </nav>

    <!-- 文章主体 -->
    <article class="container">
      <!-- 文章头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">趋势报告</span>
          <span class="tag">央企国企</span>
          <span class="tag">数字化转型</span>
        </div>
        <h1 class="article-title">2026年央企数字化转型趋势报告</h1>
        <p class="article-subtitle">基于100+央企调研 · 深度解析关键成功要素 · 典型场景与未来方向</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2026-02-20</span>
          <span><i class="far fa-clock"></i> 阅读约 15 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读卡片 -->
      <section class="intro-card">
        <div class="intro-icon">📊</div>
        <div>
          <h3>报告导读</h3>
          <p>本报告基于对100+央企的深度调研与访谈，结合国资委最新政策要求，系统梳理2026年央企数字化转型的十大趋势、五大关键领域、典型落地场景及未来展望，为央企决策者提供战略级参考。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、调研背景与方法论</a></li>
          <li><a href="#section2">二、央企数字化转型全景扫描</a></li>
          <li><a href="#section3">三、2026年十大转型趋势</a></li>
          <li><a href="#section4">四、五大关键领域深度分析</a></li>
          <li><a href="#section5">五、转型标杆企业案例</a></li>
          <li><a href="#section6">六、挑战与建议</a></li>
          <li><a href="#section7">七、未来展望</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、调研背景与方法论</h2>
          <div class="visual-block">
            <div class="visual-block-inner">
              <div class="visual-icon">🔬</div>
              <div>
                <h4>调研概况</h4>
                <p>覆盖97家央企集团及其下属400+子公司，横跨能源、金融、制造、建筑、交通、通信六大行业，历时6个月深度调研。</p>
              </div>
            </div>
          </div>
          <div class="stats-grid four-cols">
            <div class="stat-card">
              <div class="stat-number">97</div>
              <div class="stat-label">家央企集团参与调研</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">400+</div>
              <div class="stat-label">家子公司覆盖</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">200+</div>
              <div class="stat-label">位CIO/CDO深度访谈</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">6大</div>
              <div class="stat-label">行业全面覆盖</div>
            </div>
          </div>
          <p>调研方法采用"定量问卷 + 深度访谈 + 案例研究"三维模型，从战略规划、组织能力、技术架构、数据治理、业务融合五个维度，构建央企数字化转型成熟度评估体系。</p>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、央企数字化转型全景扫描</h2>
          <h3>2.1 整体成熟度分布</h3>
          <p>调研显示，央企数字化转型整体处于"规模推广"阶段，但不同行业、不同规模的企业差异显著。</p>

          <div class="maturity-chart">
            <div class="maturity-bar" v-for="item in maturityData" :key="item.label">
              <div class="maturity-label">{{ item.label }}</div>
              <div class="maturity-track">
                <div class="maturity-fill" :style="{ width: item.percent + '%', background: item.color }">
                  <span>{{ item.percent }}%</span>
                </div>
              </div>
            </div>
          </div>

          <h3>2.2 投资规模与投向</h3>
          <div class="invest-grid">
            <div class="invest-card">
              <div class="invest-icon">💰</div>
              <h4>年均数字化投入</h4>
              <div class="invest-value">营收的 2.8%</div>
              <p>较2025年增长0.5个百分点，头部企业已达4%以上</p>
            </div>
            <div class="invest-card">
              <div class="invest-icon">📈</div>
              <h4>投入增速</h4>
              <div class="invest-value">同比 +23%</div>
              <p>AI大模型相关投入增速最高，达65%</p>
            </div>
            <div class="invest-card">
              <div class="invest-icon">🎯</div>
              <h4>投资回报</h4>
              <div class="invest-value">ROI 3.2x</div>
              <p>数字化领先企业的投资回报率是行业均值的3.2倍</p>
            </div>
          </div>

          <div class="comparison-table">
            <h4>数字化投入结构变化（2024 → 2026）</h4>
            <table>
              <thead>
                <tr>
                  <th>投入方向</th>
                  <th>2024年占比</th>
                  <th>2026年占比</th>
                  <th>变化趋势</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>基础设施（云/网络）</td>
                  <td>35%</td>
                  <td>25%</td>
                  <td>↓ 基础设施成熟，投入趋稳</td>
                </tr>
                <tr>
                  <td>数据治理与中台</td>
                  <td>20%</td>
                  <td>22%</td>
                  <td>↑ 数据要素化推动持续投入</td>
                </tr>
                <tr>
                  <td>AI与大模型应用</td>
                  <td>8%</td>
                  <td>20%</td>
                  <td>↑↑ 增长最快的领域</td>
                </tr>
                <tr>
                  <td>安全合规</td>
                  <td>12%</td>
                  <td>15%</td>
                  <td>↑ 监管要求升级</td>
                </tr>
                <tr>
                  <td>业务应用系统</td>
                  <td>25%</td>
                  <td>18%</td>
                  <td>↓ 从建设转向运营优化</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、2026年十大转型趋势</h2>
          <p>基于调研数据与专家研判，我们提炼出2026年央企数字化转型的十大趋势：</p>

          <div class="trend-grid">
            <div class="trend-card" v-for="(trend, index) in trends" :key="index">
              <div class="trend-rank" :style="{ background: trend.color }">{{ index + 1 }}</div>
              <div class="trend-body">
                <h4>{{ trend.title }}</h4>
                <p>{{ trend.desc }}</p>
                <div class="trend-tag">{{ trend.impact }}</div>
              </div>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">🔥</div>
            <div>
              <h4>核心发现</h4>
              <p>AI大模型的规模化应用是2026年最显著的变化。78%的受访央企已启动大模型试点项目，其中32%已进入生产环境，应用场景从智能客服扩展到审计分析、风险预警、研发辅助等核心业务领域。</p>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、五大关键领域深度分析</h2>

          <div class="domain-section" v-for="(domain, index) in domains" :key="index">
            <div class="domain-header" :style="{ borderColor: domain.color }">
              <span class="domain-icon">{{ domain.icon }}</span>
              <div>
                <h3>{{ domain.title }}</h3>
                <p class="domain-subtitle">{{ domain.subtitle }}</p>
              </div>
            </div>
            <div class="domain-body">
              <div class="domain-metric-row">
                <div class="domain-metric" v-for="metric in domain.metrics" :key="metric.label">
                  <div class="metric-value" :style="{ color: domain.color }">{{ metric.value }}</div>
                  <div class="metric-label">{{ metric.label }}</div>
                </div>
              </div>
              <p>{{ domain.analysis }}</p>
              <div class="domain-trends">
                <span class="domain-trend-tag" v-for="tag in domain.tags" :key="tag">{{ tag }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、转型标杆企业案例</h2>
          <div class="case-grid">
            <div class="case-card">
              <div class="case-header energy">
                <span class="case-industry">能源行业</span>
                <h4>国家能源集团 —— 数据驱动的智慧能源</h4>
              </div>
              <div class="case-body">
                <div class="case-challenge">
                  <h5>🔴 转型背景</h5>
                  <p>下属600+矿井、电厂，数据分散在200+系统中，生产调度依赖人工经验，安全预警滞后，能耗优化空间大。</p>
                </div>
                <div class="case-solution">
                  <h5>🟢 关键举措</h5>
                  <p>建设集团级数据中台，部署IoT感知网络覆盖核心产线，引入AI大模型赋能安全预警与能耗优化，建立数字化人才梯队。</p>
                </div>
                <div class="case-result">
                  <h5>🏆 转型成效</h5>
                  <div class="result-metrics">
                    <span>安全事故 ↓45%</span>
                    <span>能耗降低 12%</span>
                    <span>调度效率 +60%</span>
                    <span>数据共享率 85%</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="case-card">
              <div class="case-header finance">
                <span class="case-industry">金融行业</span>
                <h4>某国有大行 —— 全面数字化银行</h4>
              </div>
              <div class="case-body">
                <div class="case-challenge">
                  <h5>🔴 转型背景</h5>
                  <p>金融科技竞争加剧，传统渠道客户流失，风控模型迭代慢，监管合规要求日趋严格，审计数字化覆盖不足。</p>
                </div>
                <div class="case-solution">
                  <h5>🟢 关键举措</h5>
                  <p>构建实时风控引擎、客户360画像平台、数据治理体系，推动全行DCMM四级认证，部署大模型智能审计助手。</p>
                </div>
                <div class="case-result">
                  <h5>🏆 转型成效</h5>
                  <div class="result-metrics">
                    <span>线上业务 92%</span>
                    <span>风控时效 毫秒级</span>
                    <span>审计覆盖 88%</span>
                    <span>合规效率 +200%</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="case-card">
              <div class="case-header manufacture">
                <span class="case-industry">制造行业</span>
                <h4>中国中车 —— 智能制造标杆</h4>
              </div>
              <div class="case-body">
                <div class="case-challenge">
                  <h5>🔴 转型背景</h5>
                  <p>产品研发周期长、供应链协同效率低、质量追溯链条断裂、设备故障导致停产损失巨大。</p>
                </div>
                <div class="case-solution">
                  <h5>🟢 关键举措</h5>
                  <p>部署数字孪生研发平台、AI质检系统、智能供应链协同平台，建设5G+工业互联网示范工厂。</p>
                </div>
                <div class="case-result">
                  <h5>🏆 转型成效</h5>
                  <div class="result-metrics">
                    <span>研发周期 ↓30%</span>
                    <span>质量合格率 99.7%</span>
                    <span>设备OEE +25%</span>
                    <span>供应链成本 ↓18%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、挑战与建议</h2>
          <h3>6.1 央企转型面临的五大挑战</h3>
          <div class="challenge-list">
            <div class="challenge-item" v-for="(challenge, index) in challenges" :key="index">
              <div class="challenge-left">
                <div class="challenge-rank">{{ index + 1 }}</div>
                <div>
                  <h4>{{ challenge.title }}</h4>
                  <p>{{ challenge.desc }}</p>
                </div>
              </div>
              <div class="challenge-percent">
                <div class="percent-bar">
                  <div class="percent-fill" :style="{ width: challenge.percent + '%' }"></div>
                </div>
                <span>{{ challenge.percent }}% 企业提及</span>
              </div>
            </div>
          </div>

          <h3>6.2 六大行动建议</h3>
          <div class="advice-grid">
            <div class="advice-card" v-for="(advice, index) in advices" :key="index">
              <div class="advice-icon">{{ advice.icon }}</div>
              <h4>{{ advice.title }}</h4>
              <p>{{ advice.desc }}</p>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、未来展望</h2>
          <div class="outlook-section">
            <div class="outlook-timeline">
              <div class="outlook-item">
                <div class="outlook-year">2026</div>
                <div class="outlook-content">
                  <h4>大模型规模化落地年</h4>
                  <p>超过60%的央企将完成至少一个核心业务场景的大模型部署，AI从辅助决策走向自主执行。</p>
                </div>
              </div>
              <div class="outlook-item">
                <div class="outlook-year">2027</div>
                <div class="outlook-content">
                  <h4>数据资产化元年</h4>
                  <p>数据要素市场化配置改革深化，央企率先实现数据资产入表，数据交易规模突破千亿。</p>
                </div>
              </div>
              <div class="outlook-item">
                <div class="outlook-year">2028</div>
                <div class="outlook-content">
                  <h4>智能体全面协同</h4>
                  <p>多智能体协同成为主流架构，业务流程实现端到端自动化，数字员工与人类员工无缝协作。</p>
                </div>
              </div>
              <div class="outlook-item">
                <div class="outlook-year">2030</div>
                <div class="outlook-content">
                  <h4>数字化原生央企</h4>
                  <p>头部央企完成数字化原生转变，数字技术深度融入每个业务环节，形成新型数字生产力。</p>
                </div>
              </div>
            </div>
          </div>

          <div class="visual-block" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">🚀</div>
              <div>
                <h4>报告结语</h4>
                <p>2026年是央企数字化转型从"建设期"迈入"深化期"的关键之年。AI大模型、数据资产化、智能审计正在重新定义央企的竞争力边界。把握趋势、务实推进、重点突破，是每一家央企决策者的当务之急。</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>获取完整版趋势报告</h2>
        <p>120页深度报告，包含全部调研数据、分析图表、案例详情及方法论工具</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约专家解读</router-link>
          <router-link to="/consulting/digital-transformation" class="btn-outline">了解转型咨询</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'SoeDigitalTrend2026',
  data() {
    return {
      maturityData: [
        { label: '战略觉醒期', percent: 8, color: '#93C5FD' },
        { label: '顶层设计期', percent: 18, color: '#60A5FA' },
        { label: '试点突破期', percent: 28, color: '#3B82F6' },
        { label: '规模推广期', percent: 32, color: '#1D4ED8' },
        { label: '生态融合期', percent: 14, color: '#0E1F52' }
      ],
      trends: [
        { title: 'AI大模型从试点到规模化落地', desc: '78%央企已启动大模型试点，应用场景从客服延伸至审计、风控、研发等核心领域。', impact: '影响力：极高', color: '#DC2626' },
        { title: '数据资产化与数据要素市场化', desc: '响应《数据要素×行动计划》，央企加速数据确权、估值、入表，探索数据交易。', impact: '影响力：极高', color: '#DC2626' },
        { title: '智能审计全面替代传统审计', desc: 'AI辅助审计覆盖率从20%提升至65%，持续审计、智能疑点识别成为标配。', impact: '影响力：高', color: '#EA580C' },
        { title: '实时风控从金融走向全行业', desc: '能源、制造等行业开始部署实时风控系统，风险预警从事后转向事前。', impact: '影响力：高', color: '#EA580C' },
        { title: '云原生架构加速国产化替代', desc: '信创国产化率目标提至80%，云原生+国产数据库成为主流技术架构。', impact: '影响力：高', color: '#EA580C' },
        { title: '数据治理从合规驱动转向价值驱动', desc: 'DCMM认证加速，数据治理目标从"治好数据"升级为"释放数据价值"。', impact: '影响力：中高', color: '#CA8A04' },
        { title: '数字孪生进入工业化部署', desc: '能源、制造、建筑行业率先实现核心资产的数字孪生，运维效率提升40%+。', impact: '影响力：中高', color: '#CA8A04' },
        { title: 'CDO/CDAO角色制度化', desc: '60%+央企设立首席数据官，数据战略从IT附属升级为董事会级议题。', impact: '影响力：中', color: '#2563EB' },
        { title: '低代码平台降低业务创新门槛', desc: '业务人员通过低代码平台自主构建应用，IT交付效率提升5-10倍。', impact: '影响力：中', color: '#2563EB' },
        { title: '网络安全与数据合规一体化', desc: '数据安全法、个人信息保护法实施深化，安全合规投入年增30%。', impact: '影响力：中', color: '#2563EB' }
      ],
      domains: [
        {
          icon: '🤖', title: '4.1 AI大模型应用', subtitle: '从辅助到核心，重塑业务流程',
          color: '#DC2626',
          metrics: [
            { value: '78%', label: '已启动试点' },
            { value: '32%', label: '进入生产环境' },
            { value: '65%', label: 'AI投入增速' }
          ],
          analysis: '大模型在央企的应用正从通用对话向垂直领域深化。审计报告自动生成、合同智能审查、安全隐患识别、研发知识问答成为最受欢迎的四大场景。RAG（检索增强生成）和智能体（Agent）架构是落地的两大关键技术。',
          tags: ['审计报告生成', '合同智能审查', '安全隐患识别', 'RAG架构', '智能体协同']
        },
        {
          icon: '📊', title: '4.2 数据治理与资产化', subtitle: '从治理合规到价值释放',
          color: '#2563EB',
          metrics: [
            { value: '45%', label: '已完成DCMM认证' },
            { value: '3.2万亿', label: '预估数据资产规模' },
            { value: '22%', label: '数据治理投入占比' }
          ],
          analysis: '数据治理正从"做标准、清数据"的合规阶段，迈入"释放价值、资产运营"的新阶段。主数据管理、数据质量监控、数据血缘追踪、数据资产目录成为四大基础能力。数据要素市场化催生了数据确权、估值和交易的新需求。',
          tags: ['DCMM认证', '主数据管理', '数据资产入表', '数据交易', '数据血缘']
        },
        {
          icon: '🔍', title: '4.3 智能审计与风控', subtitle: '持续审计与实时风控成新标配',
          color: '#059669',
          metrics: [
            { value: '65%', label: 'AI审计覆盖率' },
            { value: '3x', label: '审计效率提升' },
            { value: '92%', label: '疑点识别准确率' }
          ],
          analysis: '传统抽样审计正被持续审计全面替代。基于大数据分析的全量审计、AI自动识别疑点、审计报告自动生成"三板斧"成为智能审计标配。实时风控从银行扩展到能源、建筑行业，事前预警、过程监控、事后追溯形成闭环。',
          tags: ['持续审计', '全量数据审计', '经责审计评价', '实时风控', '穿透式监管']
        },
        {
          icon: '☁️', title: '4.4 云原生与信创', subtitle: '国产化替代进入深水区',
          color: '#7C3AED',
          metrics: [
            { value: '68%', label: '信创国产化率' },
            { value: '55%', label: '已采用云原生架构' },
            { value: '80%', label: '2027年目标国产化率' }
          ],
          analysis: '央企信创替代从办公系统向核心业务系统推进。云原生架构（容器化、微服务、DevOps）成为新建系统的默认选择。国产数据库（OceanBase、TiDB、GaussDB等）在关键场景开始替代Oracle。',
          tags: ['信创替代', '国产数据库', '容器化', '微服务', 'DevOps']
        },
        {
          icon: '🛡️', title: '4.5 安全合规', subtitle: '数据安全与隐私保护升级',
          color: '#B45309',
          metrics: [
            { value: '+30%', label: '安全投入增速' },
            { value: '85%', label: '已建立数据分级分类' },
            { value: '60%', label: '部署数据脱敏系统' }
          ],
          analysis: '数据安全法、个人信息保护法、网络安全等级保护2.0的全面实施，推动央企安全合规投入持续增长。数据分级分类、动态脱敏、访问控制、安全审计形成四道防线。AI安全（大模型安全、AI治理）成为新兴关注领域。',
          tags: ['数据分级分类', '动态脱敏', '安全审计', 'AI治理', '等保2.0']
        }
      ],
      challenges: [
        { title: '数据孤岛与系统割裂', desc: '子公司系统独立建设，数据无法互通，集团级数据汇聚困难。', percent: 82 },
        { title: '复合型数字化人才短缺', desc: '既懂业务又懂技术的人才严重不足，尤其是数据治理和AI领域。', percent: 76 },
        { title: '投入产出难以量化', desc: '数字化转型的ROI难以短期衡量，导致持续投入面临决策压力。', percent: 68 },
        { title: '组织变革阻力大', desc: '传统组织架构与数字化敏捷要求存在冲突，部门壁垒难以打破。', percent: 63 },
        { title: '安全合规压力持续增大', desc: '数据安全、信创合规、AI治理等多重要求叠加，管理复杂度上升。', percent: 58 }
      ],
      advices: [
        { icon: '🎯', title: '一把手亲自挂帅', desc: '将数字化转型上升为企业战略，CEO直接领导，纳入绩效考核体系。' },
        { icon: '📊', title: '夯实数据治理基础', desc: '先治数据、再用数据，建立企业级数据标准、质量和安全体系。' },
        { icon: '🤖', title: '拥抱AI但务实落地', desc: '选择2-3个高价值场景先行试点，验证效果后再规模化推广。' },
        { icon: '👨‍💼', title: '建设数字化人才梯队', desc: '内部培养+外部引进，重点打造数据治理师、AI工程师队伍。' },
        { icon: '🔄', title: '建立敏捷迭代机制', desc: '从瀑布式项目转向敏捷交付，小步快跑、快速验证、持续优化。' },
        { icon: '🤝', title: '构建生态合作网络', desc: '联合专业咨询机构、技术厂商，组建数字化转型"朋友圈"。' }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

/* 面包屑 */
.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

/* 头部 */
.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }
.article-meta i { margin-right: 4px; }

/* 导读 */
.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

/* 目录 */
.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { color: #0E1F52; text-decoration: underline; }

/* 正文 */
.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

/* 视觉块 */
.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; border: 1px solid #E8ECF0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

/* 统计卡片 */
.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 2.2rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

/* 对比表格 */
.comparison-table { margin: 28px 0; overflow-x: auto; }
.comparison-table h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.comparison-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
.comparison-table th { background: #0E1F52; color: #fff; padding: 12px 16px; text-align: left; font-weight: 600; }
.comparison-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; color: #3D4150; }
.comparison-table tr:nth-child(even) td { background: #F6F8FC; }

/* 成熟度图表 */
.maturity-chart { margin: 28px 0; }
.maturity-bar { display: flex; align-items: center; margin-bottom: 12px; gap: 12px; }
.maturity-label { width: 100px; font-size: 14px; color: #5A5E66; text-align: right; flex-shrink: 0; }
.maturity-track { flex: 1; height: 32px; background: #F0F2F5; border-radius: 8px; overflow: hidden; }
.maturity-fill { height: 100%; border-radius: 8px; display: flex; align-items: center; justify-content: flex-end; padding-right: 12px; transition: width 0.6s ease; }
.maturity-fill span { color: #fff; font-size: 13px; font-weight: 700; }

/* 投入卡片 */
.invest-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 28px 0; }
.invest-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; text-align: center; }
.invest-icon { font-size: 2rem; margin-bottom: 12px; }
.invest-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.invest-value { font-size: 1.6rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.invest-card p { font-size: 13px; color: #5A5E66; margin: 0; line-height: 1.5; }

/* 趋势卡片 */
.trend-grid { margin: 28px 0; }
.trend-card { display: flex; gap: 16px; background: #F6F8FC; border-radius: 12px; padding: 20px; margin-bottom: 12px; border: 1px solid #E8ECF0; transition: all 0.3s; }
.trend-card:hover { transform: translateX(4px); box-shadow: 0 4px 16px rgba(31,63,143,0.1); }
.trend-rank { width: 36px; height: 36px; border-radius: 50%; color: #fff; font-size: 15px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.trend-body h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.trend-body p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 8px; }
.trend-tag { display: inline-block; font-size: 12px; padding: 2px 10px; border-radius: 20px; background: #EBF3FE; color: #1A73E8; font-weight: 500; }

/* 高亮提示 */
.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 领域分析 */
.domain-section { margin-bottom: 32px; background: #F6F8FC; border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; }
.domain-header { display: flex; align-items: center; gap: 16px; padding: 20px 24px; border-left: 4px solid; background: #fff; }
.domain-icon { font-size: 2rem; }
.domain-header h3 { font-size: 18px; font-weight: 700; color: #0E1F52; margin: 0; }
.domain-subtitle { font-size: 14px; color: #5A5E66; margin: 4px 0 0; }
.domain-body { padding: 24px; }
.domain-metric-row { display: flex; gap: 24px; margin-bottom: 16px; }
.domain-metric { text-align: center; flex: 1; }
.metric-value { font-size: 1.8rem; font-weight: 900; margin-bottom: 4px; }
.metric-label { font-size: 13px; color: #5A5E66; }
.domain-body > p { font-size: 14px; color: #3D4150; line-height: 1.7; }
.domain-trends { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.domain-trend-tag { background: #EBF3FE; color: #1A73E8; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }

/* 案例 */
.case-grid { margin: 28px 0; }
.case-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; margin-bottom: 24px; transition: all 0.3s; }
.case-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(31,63,143,0.12); }
.case-header { padding: 20px 24px; color: #fff; }
.case-header.energy { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); }
.case-header.finance { background: linear-gradient(135deg, #0D7C66 0%, #094B3E 100%); }
.case-header.manufacture { background: linear-gradient(135deg, #B45309 0%, #78350F 100%); }
.case-industry { background: rgba(255,255,255,0.2); padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }
.case-header h4 { font-size: 18px; font-weight: 700; margin-top: 10px; }
.case-body { padding: 24px; }
.case-body h5 { font-size: 14px; font-weight: 700; margin-bottom: 8px; }
.case-body p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 16px; }
.result-metrics { display: flex; gap: 12px; flex-wrap: wrap; }
.result-metrics span { background: #EBF3FE; color: #1A73E8; padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: 600; }

/* 挑战 */
.challenge-list { margin: 24px 0; }
.challenge-item { display: flex; align-items: center; justify-content: space-between; gap: 24px; background: #F6F8FC; border-radius: 12px; padding: 20px 24px; margin-bottom: 12px; border: 1px solid #E8ECF0; }
.challenge-left { display: flex; gap: 16px; align-items: flex-start; flex: 1; }
.challenge-rank { width: 32px; height: 32px; background: #0E1F52; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; flex-shrink: 0; }
.challenge-left h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 4px; }
.challenge-left p { font-size: 13px; color: #5A5E66; line-height: 1.5; margin: 0; }
.challenge-percent { width: 200px; flex-shrink: 0; text-align: right; }
.percent-bar { height: 8px; background: #E8ECF0; border-radius: 4px; margin-bottom: 6px; overflow: hidden; }
.percent-fill { height: 100%; background: linear-gradient(90deg, #1A73E8, #0E1F52); border-radius: 4px; }
.challenge-percent span { font-size: 12px; color: #5A5E66; }

/* 建议 */
.advice-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.advice-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; transition: all 0.3s; }
.advice-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.advice-icon { font-size: 2rem; margin-bottom: 12px; }
.advice-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.advice-card p { font-size: 13px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 展望时间线 */
.outlook-timeline { position: relative; padding-left: 40px; }
.outlook-timeline::before { content: ''; position: absolute; left: 19px; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom, #4A90D9, #0E1F52); border-radius: 2px; }
.outlook-item { position: relative; margin-bottom: 28px; }
.outlook-item:last-child { margin-bottom: 0; }
.outlook-year { position: absolute; left: -40px; width: 38px; height: 38px; background: #1A73E8; border-radius: 50%; color: #fff; font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center; z-index: 1; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
.outlook-content { background: #F6F8FC; border-radius: 12px; padding: 20px 24px; border: 1px solid #E8ECF0; }
.outlook-content h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.outlook-content p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* CTA */
.cta-section { text-align: center; background:#f8f9f9; color: #fff; border-radius: 16px; padding: 48px 32px; margin-bottom: 60px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 16px; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #1A73E8; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; transition: all 0.3s; }
.btn-primary:hover { background: #1557B0; }
.btn-outline { background: transparent; color:#E8ECF0; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; border: 2px solid rgba(255,255,255,0.5); transition: all 0.3s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

/* 响应式 */
@media (max-width: 768px) {
  .article-title { font-size: 1.6rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .invest-grid { grid-template-columns: 1fr; }
  .advice-grid { grid-template-columns: repeat(2, 1fr); }
  .domain-metric-row { flex-wrap: wrap; }
  .challenge-item { flex-direction: column; }
  .challenge-percent { width: 100%; text-align: left; }
  .article-meta { flex-wrap: wrap; justify-content: center; }
  .cta-buttons { flex-direction: column; align-items: center; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .advice-grid { grid-template-columns: 1fr; }
}
</style>
